import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SupabaseContext } from '../App';

function CreateQuiz() {
  const { supabase, session } = useContext(SupabaseContext);
  const [questions, setQuestions] = useState([{ question: '', a: '', b: '', c: '', d: '', correct: '' }]);
  const [timeOption, setTimeOption] = useState('noLimit');
  const [timeDuration, setTimeDuration] = useState('');
  const [attemptOption, setAttemptOption] = useState('multiple');
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const editQuizCode = queryParams.get('edit');
  const isEditMode = !!editQuizCode;

  useEffect(() => {
    if (isEditMode) {
      const fetchQuizData = async () => {
        try {
          const { data: quiz, error: quizError } = await supabase
            .from('quizzes')
            .select('id, code, time_limit, attempt_limit')
            .eq('code', editQuizCode)
            .eq('user_id', session.user.id)
            .single();
          if (quizError) throw quizError;

          const { data: questionsData, error: questionsError } = await supabase
            .from('questions')
            .select('id, question, a, b, c, d, correct')
            .eq('quiz_id', quiz.id);
          if (questionsError) throw questionsError;

          setQuestions(questionsData.length ? questionsData : [{ question: '', a: '', b: '', c: '', d: '', correct: '' }]);
          if (quiz.time_limit) {
            setTimeOption('duration');
            setTimeDuration(quiz.time_limit.toString());
          }
          setAttemptOption(quiz.attempt_limit ? 'single' : 'multiple');
        } catch (error) {
          alert('Error loading quiz for edit: ' + error.message);
          navigate('/history');
        }
      };
      fetchQuizData();
    }
  }, [supabase, session, editQuizCode, isEditMode, navigate]);

  const addQuestion = () => setQuestions([...questions, { question: '', a: '', b: '', c: '', d: '', correct: '' }]);

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const handleDeleteQuestion = (index) => {
    if (questions.length <= 1) {
      alert('You must have at least one question in the quiz.');
      return;
    }
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!session) return alert('Please log in to create or edit a quiz.');
    if (questions.some(q => !q.question || !q.a || !q.b || !q.c || !q.d || !q.correct)) return alert('Please fill all fields for all questions.');
    if (timeOption === 'duration' && (!timeDuration || parseInt(timeDuration) <= 0)) return alert('Please enter a valid time duration in minutes.');

    try {
      if (isEditMode) {
        const { data: quiz, error: quizError } = await supabase
          .from('quizzes')
          .update({ 
            time_limit: timeOption === 'duration' ? parseInt(timeDuration) : null,
            attempt_limit: attemptOption === 'single'
          })
          .eq('code', editQuizCode)
          .eq('user_id', session.user.id)
          .select('id')
          .single();
        if (quizError) throw quizError;

        const existingQuestionIds = questions.filter(q => q.id).map(q => q.id);
        const { data: oldQuestions } = await supabase.from('questions').select('id').eq('quiz_id', quiz.id);
        const deletedQuestionIds = oldQuestions.filter(q => !existingQuestionIds.includes(q.id)).map(q => q.id);
        if (deletedQuestionIds.length > 0) await supabase.from('questions').delete().in('id', deletedQuestionIds);

        const updatedQuestions = questions.map(q => ({ id: q.id, quiz_id: quiz.id, question: q.question, a: q.a, b: q.b, c: q.c, d: q.d, correct: q.correct }));
        const { error: questionsError } = await supabase.from('questions').upsert(updatedQuestions, { onConflict: 'id' });
        if (questionsError) throw questionsError;

        alert(`Quiz ${editQuizCode} updated successfully!`);
      } else {
        const quizCode = Math.random().toString(36).substring(2, 7).toUpperCase();
        const { data: quiz, error: quizError } = await supabase
          .from('quizzes')
          .insert([{ 
            code: quizCode, 
            time_limit: timeOption === 'duration' ? parseInt(timeDuration) : null,
            user_id: session.user.id,
            attempt_limit: attemptOption === 'single'
          }])
          .select('id')
          .single();
        if (quizError) throw quizError;

        const questionsData = questions.map(q => ({ quiz_id: quiz.id, question: q.question, a: q.a, b: q.b, c: q.c, d: q.d, correct: q.correct }));
        const { error: questionsError } = await supabase.from('questions').insert(questionsData);
        if (questionsError) throw questionsError;

        alert(`Quiz created! Code: ${quizCode}`);
      }
      navigate('/history');
    } catch (error) {
      alert('Error saving quiz: ' + error.message);
    }
  };

  if (!session) {
    navigate('/');
    return null;
  }

  return (
    <div className="page-container">
      <div className="auth-card">
        <h2>{isEditMode ? `Edit Quiz: ${editQuizCode}` : 'Create a Quiz'}</h2>
        {questions.map((q, index) => (
          <div key={q.id || index} className="question-card">
            <div className="form-group">
              <label>Question {index + 1}</label>
              <input
                type="text"
                value={q.question}
                onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                placeholder={`Enter question ${index + 1}`}
              />
            </div>
            <div className="form-group">
              <label>Option A</label>
              <input
                type="text"
                value={q.a}
                onChange={(e) => handleQuestionChange(index, 'a', e.target.value)}
                placeholder="Enter option A"
              />
            </div>
            <div className="form-group">
              <label>Option B</label>
              <input
                type="text"
                value={q.b}
                onChange={(e) => handleQuestionChange(index, 'b', e.target.value)}
                placeholder="Enter option B"
              />
            </div>
            <div className="form-group">
              <label>Option C</label>
              <input
                type="text"
                value={q.c}
                onChange={(e) => handleQuestionChange(index, 'c', e.target.value)}
                placeholder="Enter option C"
              />
            </div>
            <div className="form-group">
              <label>Option D</label>
              <input
                type="text"
                value={q.d}
                onChange={(e) => handleQuestionChange(index, 'd', e.target.value)}
                placeholder="Enter option D"
              />
            </div>
            <div className="form-group">
              <label>Correct Answer</label>
              <select
                value={q.correct}
                onChange={(e) => handleQuestionChange(index, 'correct', e.target.value)}
              >
                <option value="">Select Correct Answer</option>
                <option value="a">A</option>
                <option value="b">B</option>
                <option value="c">C</option>
                <option value="d">D</option>
              </select>
            </div>
            <div className="button-group">
              <button className="delete-btn" onClick={() => handleDeleteQuestion(index)}>
                Delete Question
              </button>
            </div>
          </div>
        ))}
        <div className="button-group">
          <button onClick={addQuestion}>Add Question</button>
        </div>
        <div className="form-group">
          <h3>Quiz Time Settings</h3>
          <label className="radio-label">
            <input
              type="radio"
              value="duration"
              checked={timeOption === 'duration'}
              onChange={() => setTimeOption('duration')}
            />
            Time Duration (minutes):
            <input
              type="number"
              value={timeDuration}
              onChange={(e) => setTimeDuration(e.target.value)}
              disabled={timeOption !== 'duration'}
              min="1"
              placeholder="e.g., 10"
            />
          </label>
          <label className="radio-label">
            <input
              type="radio"
              value="noLimit"
              checked={timeOption === 'noLimit'}
              onChange={() => setTimeOption('noLimit')}
            />
            No Time Limit
          </label>
        </div>
        <div className="form-group">
          <h3>Attempt Settings</h3>
          <label className="radio-label">
            <input
              type="radio"
              value="single"
              checked={attemptOption === 'single'}
              onChange={() => setAttemptOption('single')}
            />
            Single Attempt
          </label>
          <label className="radio-label">
            <input
              type="radio"
              value="multiple"
              checked={attemptOption === 'multiple'}
              onChange={() => setAttemptOption('multiple')}
            />
            Multiple Attempts
          </label>
        </div>
        <div className="button-group">
          <button onClick={handleSubmit}>{isEditMode ? 'Save Changes' : 'Submit Quiz'}</button>
        </div>
      </div>
    </div>
  );
}

export default CreateQuiz;